import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { TrendingUp, Users, MousePointerClick, LogOut, Monitor, Globe, Activity, MapPin, Languages, Radio } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#7A00EB', '#00B5B4', '#ec008c', '#f59e0b', '#6366f1', '#10b981', '#ef4444', '#8b5cf6'];

export const AdminAnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadRealtime = useCallback(() => {
    fetch(`${API_URL}/api/admin/analytics/realtime`).then(r => r.json()).then(setRealtime).catch(() => {});
  }, []);

  useEffect(() => {
    const loadAll = () => Promise.all([
      fetch(`${API_URL}/api/admin/analytics/dashboard`).then(r => r.json()),
      fetch(`${API_URL}/api/admin/analytics/realtime`).then(r => r.json()),
    ]).then(([d, rt]) => { setData(d); setRealtime(rt); }).catch(() => {}).finally(() => setLoading(false));

    loadAll();
    const rtInterval = setInterval(loadRealtime, 15000);
    const dashInterval = setInterval(() => {
      fetch(`${API_URL}/api/admin/analytics/dashboard`).then(r => r.json()).then(setData).catch(() => {});
    }, 60000);
    return () => { clearInterval(rtInterval); clearInterval(dashInterval); };
  }, [loadRealtime]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading analytics...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">Failed to load analytics</div>;

  const { summary = {}, funnel = {}, abandonment_detail = [], traffic_sources = [], devices = [], browsers = [], os_breakdown = [], countries = [], cities = [], languages = [], daily_visitors = [] } = data;

  const funnelData = [
    { name: 'Quiz Started', value: funnel.quiz_start || 0, fill: '#7A00EB' },
    { name: 'Section 1 Done', value: funnel.section_1_complete || 0, fill: '#6366f1' },
    { name: 'Section 2 Done', value: funnel.section_2_complete || 0, fill: '#00B5B4' },
    { name: 'Section 3 Done', value: funnel.section_3_complete || 0, fill: '#10b981' },
    { name: 'Quiz Submitted', value: funnel.quiz_submit || 0, fill: '#ec008c' },
    { name: 'Report Generated', value: funnel.report_generate || 0, fill: '#f59e0b' },
  ];

  const completionRate = funnel.quiz_start > 0 ? ((funnel.quiz_submit / funnel.quiz_start) * 100).toFixed(1) : '0';
  const abandonmentRate = funnel.quiz_start > 0 ? (((funnel.quiz_start - funnel.quiz_submit) / funnel.quiz_start) * 100).toFixed(1) : '0';
  const activeNow = realtime?.active_users ?? summary.active_now ?? 0;

  return (
    <div className="space-y-6" data-testid="admin-analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-2 border-green-300 bg-green-50">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <Radio className="h-5 w-5 text-green-600 animate-pulse" />
              <div>
                <div className="text-3xl font-bold text-green-700" data-testid="active-users-count">{activeNow}</div>
                <div className="text-xs text-green-600 font-medium">Live Now</div>
              </div>
            </div>
            {realtime?.by_page && Object.keys(realtime.by_page).length > 0 && (
              <div className="mt-2 text-xs text-green-700 space-y-0.5">
                {Object.entries(realtime.by_page).map(([pg, cnt]) => <div key={pg}>{pg}: {cnt}</div>)}
              </div>
            )}
          </CardContent>
        </Card>
        <StatCard icon={<Activity className="h-5 w-5" />} label="Events Today" value={summary.today_events} color="text-purple-600" />
        <StatCard icon={<Users className="h-5 w-5" />} label="Quizzes Started (30d)" value={summary.quizzes_started} color="text-cyan-600" />
        <StatCard icon={<MousePointerClick className="h-5 w-5" />} label="Completed (30d)" value={summary.quizzes_completed} color="text-emerald-600" />
        <StatCard icon={<LogOut className="h-5 w-5" />} label="Abandoned (30d)" value={summary.abandoned} color="text-red-500" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold text-emerald-600">{completionRate}%</div><div className="text-sm text-gray-500 mt-1">Completion Rate</div></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="text-3xl font-bold text-red-500">{abandonmentRate}%</div><div className="text-sm text-gray-500 mt-1">Abandonment Rate</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Quiz Funnel (30 Days)</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {funnelData.map((step, i) => {
              const maxVal = funnelData[0].value || 1;
              const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
              const dropoff = i > 0 && funnelData[i - 1].value > 0 ? (((funnelData[i - 1].value - step.value) / funnelData[i - 1].value) * 100).toFixed(0) : null;
              return (
                <div key={step.name} className="flex items-center gap-3">
                  <div className="w-36 text-sm text-gray-600 shrink-0">{step.name}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-7 overflow-hidden relative">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%`, background: step.fill }} />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-700">{step.value}</span>
                  </div>
                  {dropoff !== null && <span className="text-xs text-red-400 w-16 text-right shrink-0">-{dropoff}%</span>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {abandonment_detail && abandonment_detail.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><LogOut className="h-5 w-5 text-red-500" /> Drop-off Points</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {abandonment_detail.slice(0, 10).map((a) => (
                <div key={`drop-${a.section}-${a.question}`} className="flex items-center justify-between text-sm border-b border-gray-50 pb-1">
                  <span className="text-gray-600">Section {a.section}, Question {a.question + 1}</span>
                  <span className="font-semibold text-red-500">{a.count} drop-offs</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {daily_visitors.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Globe className="h-5 w-5" /> Daily Page Views (30 Days)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={daily_visitors}>
                <defs><linearGradient id="colorV" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7A00EB" stopOpacity={0.3}/><stop offset="95%" stopColor="#7A00EB" stopOpacity={0}/></linearGradient></defs>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="visitors" stroke="#7A00EB" fill="url(#colorV)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5" /> Top Countries</CardTitle></CardHeader>
          <CardContent>
            {(!countries || countries.length === 0) ? <p className="text-sm text-gray-400 text-center py-4">No location data yet.</p> : (
              <div className="space-y-2">{countries.map((c) => (<div key={`country-${c.country}`} className="flex items-center justify-between text-sm"><span className="text-gray-700">{c.country}</span><span className="font-medium text-gray-900">{c.count}</span></div>))}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5" /> Top Cities</CardTitle></CardHeader>
          <CardContent>
            {(!cities || cities.length === 0) ? <p className="text-sm text-gray-400 text-center py-4">No city data yet.</p> : (
              <div className="space-y-2">{cities.map((c) => (<div key={`city-${c.country}-${c.city}`} className="flex items-center justify-between text-sm"><span className="text-gray-700">{c.city}, {c.country}</span><span className="font-medium text-gray-900">{c.count}</span></div>))}</div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Languages className="h-5 w-5" /> Languages</CardTitle></CardHeader>
          <CardContent>
            {(!languages || languages.length === 0) ? <p className="text-sm text-gray-400 text-center py-4">No language data yet.</p> : (
              <div className="space-y-2">{languages.map((l) => (<div key={`lang-${l.language}`} className="flex items-center justify-between text-sm"><span className="text-gray-700">{l.language}</span><span className="font-medium text-gray-900">{l.count}</span></div>))}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg">Operating Systems</CardTitle></CardHeader>
          <CardContent>
            {(!os_breakdown || os_breakdown.length === 0) ? <p className="text-sm text-gray-400 text-center py-4">No OS data yet.</p> : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart><Pie data={os_breakdown} dataKey="count" nameKey="os" cx="50%" cy="50%" outerRadius={70} label={({ os, percent }) => `${os} ${(percent * 100).toFixed(0)}%`}>{os_breakdown.map((entry, i) => <Cell key={`os-${entry.os}`} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Traffic Sources</CardTitle></CardHeader>
          <CardContent>
            {traffic_sources.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No traffic data yet.</p> : (
              <div className="space-y-2">{traffic_sources.map((src) => (<div key={`src-${src.source}`} className="flex items-center justify-between text-sm"><span className="text-gray-700 truncate max-w-[200px]">{src.source}</span><span className="font-medium text-gray-900">{src.count}</span></div>))}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Monitor className="h-5 w-5" /> Devices</CardTitle></CardHeader>
          <CardContent>
            {devices.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No device data yet.</p> : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart><Pie data={devices} dataKey="count" nameKey="device" cx="50%" cy="50%" outerRadius={70} label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}>{devices.map((entry, i) => <Cell key={`device-${entry.device}`} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {browsers.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Browsers</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={browsers} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="browser" tick={{ fontSize: 11 }} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#00B5B4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <Card>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center gap-3">
        <div className={color}>{icon}</div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value ?? 0}</div>
          <div className="text-xs text-gray-500">{label}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);
